(() => {
  const SCRIPT_SELECTOR = 'script[data-casekoo-gallery-analytics]';
  const ROOT_SELECTOR = '[data-casekoo-gallery-tracker]';
  const EVENT_NAME = 'pdp_gallery_image_view';
  const SUMMARY_EVENT_NAME = 'pdp_gallery_session_summary';
  const SCROLL_EVENT_NAME = 'pdp_scroll_depth';
  const SCROLL_STARTED_EVENT_NAME = 'pdp_scroll_started';
  const SCROLL_SUMMARY_EVENT_NAME = 'pdp_scroll_session_summary';
  const SHOPIFY_EVENT_NAME = 'casekoo:product_gallery_image_viewed';
  const SCROLL_THRESHOLDS = [25, 50, 75, 90, 100];
  const SCROLL_MOVEMENT_THRESHOLDS = [5];
  const SCROLL_STARTED_MIN_PIXELS = 10;
  const SETTLE_DELAY_MS = 650;
  const INITIAL_DELAY_MS = 1200;

  if (window.__CASEKOO_PRODUCT_GALLERY_ANALYTICS__) return;

  const script = document.querySelector(SCRIPT_SELECTOR);
  const measurementId =
    script?.dataset.casekooMeasurementId?.trim() || 'G-3P80DVND27';

  if (!measurementId) return;

  const state = {
    ready: false,
    seenMediaIds: new Set(),
    maxPosition: 0,
    lastInteractionMethod: 'initial',
    pendingPayloads: [],
    settleTimer: null,
    summarySent: false,
    root: null,
    privacyRetryTimer: null,
    privacyRetryAttempts: 0,
    reachedScrollThresholds: new Set(),
    reachedScrollMovementThresholds: new Set(),
    scrollStarted: false,
    maxScrollPercent: 0,
    maxScrollPixels: 0,
    scrollPageHeight: 0,
    scrollRaf: null,
    scrollSummarySent: false,
  };

  const updateDebugState = () => {
    if (!state.root) return;

    state.root.dataset.casekooGalleryAnalyticsStatus = state.ready
      ? 'ready'
      : 'initializing';
    state.root.dataset.casekooGalleryUniqueCount = String(
      state.seenMediaIds.size
    );
    state.root.dataset.casekooGalleryMaxPosition = String(state.maxPosition);
    state.root.dataset.casekooGalleryPendingEvents = String(
      state.pendingPayloads.length
    );
    state.root.dataset.casekooScrollMaxPercent = String(
      state.maxScrollPercent
    );
    state.root.dataset.casekooScrollMaxPixels = String(state.maxScrollPixels);
    state.root.dataset.casekooScrollThresholds = Array.from(
      state.reachedScrollThresholds
    ).join(',');
    state.root.dataset.casekooScrollMovementThresholds = Array.from(
      state.reachedScrollMovementThresholds
    ).join(',');
    state.root.dataset.casekooScrollStarted = String(state.scrollStarted);
  };

  const analyticsAllowed = () => {
    const customerPrivacy = window.Shopify?.customerPrivacy;

    if (
      customerPrivacy &&
      typeof customerPrivacy.analyticsProcessingAllowed === 'function'
    ) {
      return customerPrivacy.analyticsProcessingAllowed();
    }

    return false;
  };

  const ensureGoogleTag = () => {
    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function gtag() {
        window.dataLayer.push(arguments);
      };

    if (!window.__CASEKOO_GA4_GALLERY_CONFIGURED__) {
      window.__CASEKOO_GA4_GALLERY_CONFIGURED__ = true;
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        send_page_view: false,
      });
    }

    const correctGoogleTagLoaded = Array.from(document.scripts).some(
      (tagScript) =>
        tagScript.dataset.casekooGalleryGtag === measurementId ||
        tagScript.src.includes(
          `googletagmanager.com/gtag/js?id=${measurementId}`
        )
    );

    if (state.root) {
      state.root.dataset.casekooGalleryTagStatus = correctGoogleTagLoaded
        ? 'existing'
        : 'loading';
    }

    if (
      !correctGoogleTagLoaded &&
      !document.querySelector(
        `script[data-casekoo-gallery-gtag="${measurementId}"]`
      )
    ) {
      const googleTagScript = document.createElement('script');
      googleTagScript.async = true;
      googleTagScript.dataset.casekooGalleryGtag = measurementId;
      googleTagScript.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
        measurementId
      )}`;
      googleTagScript.addEventListener('load', () => {
        if (state.root) {
          state.root.dataset.casekooGalleryTagStatus = 'loaded';
        }
      });
      googleTagScript.addEventListener('error', () => {
        if (state.root) {
          state.root.dataset.casekooGalleryTagStatus = 'error';
        }
      });
      document.head.appendChild(googleTagScript);
    }
  };

  const sendToGoogleAnalytics = (eventName, payload) => {
    if (!analyticsAllowed()) {
      state.pendingPayloads.push({ eventName, payload });
      updateDebugState();
      schedulePrivacyRetry();
      return;
    }

    ensureGoogleTag();
    if (state.root) {
      state.root.dataset.casekooGalleryLastDispatch = eventName;
    }
    window.gtag('event', eventName, {
      ...payload,
      send_to: measurementId,
      transport_type: 'beacon',
      event_callback: () => {
        if (state.root) {
          state.root.dataset.casekooGalleryLastAcknowledged = eventName;
        }
      },
      event_timeout: 2000,
    });
    updateDebugState();
  };

  const flushPendingPayloads = () => {
    if (!analyticsAllowed() || state.pendingPayloads.length === 0) return;

    const queuedPayloads = state.pendingPayloads.splice(0);
    queuedPayloads.forEach(({ eventName, payload }) => {
      sendToGoogleAnalytics(eventName, payload);
    });
    updateDebugState();
  };

  function schedulePrivacyRetry() {
    if (state.privacyRetryTimer) return;

    state.privacyRetryTimer = window.setInterval(() => {
      state.privacyRetryAttempts += 1;
      flushPendingPayloads();

      if (analyticsAllowed() || state.privacyRetryAttempts >= 30) {
        window.clearInterval(state.privacyRetryTimer);
        state.privacyRetryTimer = null;
      }
    }, 1000);
  }

  const isVisible = (element) => {
    if (!element) return false;

    const styles = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    return (
      styles.display !== 'none' &&
      styles.visibility !== 'hidden' &&
      rect.width > 0 &&
      rect.height > 0
    );
  };

  const getCurrentSlide = (root) => {
    const slides = Array.from(
      root.querySelectorAll(
        '.feature-media-item[data-media-id][data-casekoo-gallery-position]'
      )
    );

    return (
      slides.find(
        (slide) =>
          slide.classList.contains('is-active') &&
          slide.getAttribute('aria-hidden') !== 'true' &&
          isVisible(slide)
      ) ||
      slides.find(
        (slide) =>
          slide.getAttribute('aria-hidden') === 'false' && isVisible(slide)
      ) ||
      slides.find(isVisible) ||
      null
    );
  };

  const publishShopifyEvent = (payload) => {
    if (typeof window.Shopify?.analytics?.publish !== 'function') return;

    window.Shopify.analytics.publish(SHOPIFY_EVENT_NAME, payload);
  };

  const buildBasePayload = (root) => ({
    product_id: root.dataset.casekooProductId || '',
    product_handle: root.dataset.casekooProductHandle || '',
    total_gallery_images: Number(root.dataset.casekooTotalMedia || 0),
  });

  const trackCurrentSlide = (root, method = state.lastInteractionMethod) => {
    const slide = getCurrentSlide(root);
    if (!slide) return;

    const mediaId = slide.dataset.mediaId || '';
    const position = Number(slide.dataset.casekooGalleryPosition || 0);

    if (!mediaId || position < 1 || state.seenMediaIds.has(mediaId)) return;

    state.seenMediaIds.add(mediaId);
    state.maxPosition = Math.max(state.maxPosition, position);
    state.root.dataset.casekooGalleryLastPosition = String(position);
    state.root.dataset.casekooGalleryLastMethod = method;

    const payload = {
      ...buildBasePayload(root),
      media_id: mediaId,
      gallery_image_position: position,
      gallery_interaction_method: method,
      gallery_unique_count: state.seenMediaIds.size,
      gallery_max_position: state.maxPosition,
    };

    if (
      new URLSearchParams(window.location.search).get('ck_gallery_debug') ===
      '1'
    ) {
      payload.debug_mode = true;
    }

    publishShopifyEvent(payload);
    sendToGoogleAnalytics(EVENT_NAME, payload);
    updateDebugState();

    state.lastInteractionMethod = 'programmatic';
  };

  const scheduleTrack = (root, method = state.lastInteractionMethod) => {
    window.clearTimeout(state.settleTimer);
    state.settleTimer = window.setTimeout(
      () => trackCurrentSlide(root, method),
      SETTLE_DELAY_MS
    );
  };

  const getScrollDepth = () => {
    const documentElement = document.documentElement;
    const body = document.body;
    const pageHeight = Math.max(
      documentElement?.scrollHeight || 0,
      body?.scrollHeight || 0
    );
    const viewportHeight =
      window.innerHeight || documentElement?.clientHeight || 0;
    const visibleBottom = Math.min(
      pageHeight,
      Math.max(0, window.scrollY || window.pageYOffset || 0) + viewportHeight
    );
    const percent =
      pageHeight > 0
        ? Math.min(
            100,
            Math.max(0, Math.round((visibleBottom / pageHeight) * 100))
          )
        : 0;
    const scrollOffset = Math.max(
      0,
      Math.round(window.scrollY || window.pageYOffset || 0)
    );
    const scrollableDistance = Math.max(0, pageHeight - viewportHeight);
    const movementPercent =
      scrollableDistance > 0
        ? Math.min(
            100,
            Math.max(0, Math.round((scrollOffset / scrollableDistance) * 100))
          )
        : 0;

    return {
      percent,
      movementPercent,
      scrollOffset,
      scrollableDistance: Math.round(scrollableDistance),
      pixels: Math.round(visibleBottom),
      pageHeight: Math.round(pageHeight),
      viewportHeight: Math.round(viewportHeight),
    };
  };

  const addDebugMode = (payload) => {
    if (
      new URLSearchParams(window.location.search).get('ck_gallery_debug') ===
      '1'
    ) {
      payload.debug_mode = true;
    }

    return payload;
  };

  const trackScrollDepth = (root) => {
    const depth = getScrollDepth();

    if (
      !state.scrollStarted &&
      depth.scrollOffset >= SCROLL_STARTED_MIN_PIXELS
    ) {
      state.scrollStarted = true;
      sendToGoogleAnalytics(
        SCROLL_STARTED_EVENT_NAME,
        addDebugMode({
          ...buildBasePayload(root),
          scroll_offset_pixels: depth.scrollOffset,
          scroll_page_height: depth.pageHeight,
          scroll_viewport_height: depth.viewportHeight,
        })
      );
    }

    state.maxScrollPercent = Math.max(state.maxScrollPercent, depth.percent);
    state.maxScrollPixels = Math.max(state.maxScrollPixels, depth.pixels);
    state.scrollPageHeight = Math.max(
      state.scrollPageHeight,
      depth.pageHeight
    );

    SCROLL_THRESHOLDS.forEach((threshold) => {
      if (
        depth.percent < threshold ||
        state.reachedScrollThresholds.has(threshold)
      ) {
        return;
      }

      state.reachedScrollThresholds.add(threshold);

      sendToGoogleAnalytics(
        SCROLL_EVENT_NAME,
        addDebugMode({
          ...buildBasePayload(root),
          scroll_depth_percent: threshold,
          scroll_max_percent: state.maxScrollPercent,
          scroll_depth_pixels: depth.pixels,
          scroll_page_height: depth.pageHeight,
          scroll_viewport_height: depth.viewportHeight,
        })
      );
    });

    SCROLL_MOVEMENT_THRESHOLDS.forEach((threshold) => {
      if (
        depth.movementPercent < threshold ||
        state.reachedScrollMovementThresholds.has(threshold)
      ) {
        return;
      }

      state.reachedScrollMovementThresholds.add(threshold);

      sendToGoogleAnalytics(
        SCROLL_EVENT_NAME,
        addDebugMode({
          ...buildBasePayload(root),
          scroll_depth_percent: threshold,
          scroll_measurement_basis: 'scrollable_distance',
          scroll_movement_percent: depth.movementPercent,
          scroll_offset_pixels: depth.scrollOffset,
          scrollable_distance_pixels: depth.scrollableDistance,
          scroll_page_height: depth.pageHeight,
          scroll_viewport_height: depth.viewportHeight,
        })
      );
    });

    updateDebugState();
  };

  const scheduleScrollTrack = (root) => {
    if (state.scrollRaf !== null) return;

    state.scrollRaf = window.requestAnimationFrame(() => {
      state.scrollRaf = null;
      trackScrollDepth(root);
    });
  };

  const sendScrollSummary = (root) => {
    if (state.scrollSummarySent) return;

    trackScrollDepth(root);
    state.scrollSummarySent = true;

    sendToGoogleAnalytics(
      SCROLL_SUMMARY_EVENT_NAME,
      addDebugMode({
        ...buildBasePayload(root),
        scroll_max_percent: state.maxScrollPercent,
        scroll_started: state.scrollStarted,
        scroll_movement_thresholds: Array.from(
          state.reachedScrollMovementThresholds
        ).join(','),
        scroll_depth_pixels: state.maxScrollPixels,
        scroll_page_height: state.scrollPageHeight,
      })
    );
  };

  const sendSummary = (root) => {
    if (state.summarySent || state.seenMediaIds.size === 0) return;
    state.summarySent = true;

    const payload = {
      ...buildBasePayload(root),
      gallery_unique_count: state.seenMediaIds.size,
      gallery_max_position: state.maxPosition,
    };

    if (
      new URLSearchParams(window.location.search).get('ck_gallery_debug') ===
      '1'
    ) {
      payload.debug_mode = true;
    }

    sendToGoogleAnalytics(SUMMARY_EVENT_NAME, payload);
  };

  const init = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;

    state.root = root;
    updateDebugState();

    let touchStartX = null;
    let pointerStartX = null;

    root.addEventListener(
      'pointerdown',
      (event) => {
        pointerStartX = event.clientX;
        state.lastInteractionMethod = 'swipe';
      },
      { passive: true }
    );

    root.addEventListener(
      'pointerup',
      (event) => {
        if (
          pointerStartX !== null &&
          Math.abs(event.clientX - pointerStartX) >= 30
        ) {
          scheduleTrack(root, 'swipe');
        } else {
          state.lastInteractionMethod = 'programmatic';
        }

        pointerStartX = null;
      },
      { passive: true }
    );

    root.addEventListener(
      'pointercancel',
      () => {
        pointerStartX = null;
        state.lastInteractionMethod = 'programmatic';
      },
      { passive: true }
    );

    root.addEventListener(
      'touchstart',
      (event) => {
        touchStartX = event.touches?.[0]?.clientX ?? null;
        state.lastInteractionMethod = 'swipe';
      },
      { passive: true }
    );

    root.addEventListener(
      'touchend',
      (event) => {
        const touchEndX = event.changedTouches?.[0]?.clientX ?? null;

        if (
          touchStartX !== null &&
          touchEndX !== null &&
          Math.abs(touchEndX - touchStartX) >= 30
        ) {
          state.lastInteractionMethod = 'swipe';
          scheduleTrack(root, 'swipe');
        }

        touchStartX = null;
      },
      { passive: true }
    );

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        if (
          target.closest(
            '.product-thumbnail-list-item, [id^="slideshow-mobile-thumbnails-"]'
          )
        ) {
          state.lastInteractionMethod = 'thumbnail';
          scheduleTrack(root, 'thumbnail');
          return;
        }

        if (
          target.closest('.splide__arrow, .casekoo-thumbnail-arrow, .push-btn')
        ) {
          state.lastInteractionMethod = 'arrow';
          scheduleTrack(root, 'arrow');
        }
      },
      true
    );

    const observer = new MutationObserver(() => scheduleTrack(root));
    observer.observe(root, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'class', 'style'],
    });

    window.addEventListener('pagehide', () => sendSummary(root), {
      capture: true,
    });
    window.addEventListener('pagehide', () => sendScrollSummary(root), {
      capture: true,
    });

    window.addEventListener(
      'scroll',
      () => scheduleScrollTrack(root),
      { passive: true }
    );
    window.addEventListener(
      'resize',
      () => scheduleScrollTrack(root),
      { passive: true }
    );

    document.addEventListener('visitorConsentCollected', flushPendingPayloads);
    window.addEventListener('visitorConsentCollected', flushPendingPayloads);

    window.setTimeout(() => {
      state.ready = true;
      trackCurrentSlide(root, 'initial');
      trackScrollDepth(root);
      flushPendingPayloads();
      updateDebugState();
    }, INITIAL_DELAY_MS);
  };

  window.__CASEKOO_PRODUCT_GALLERY_ANALYTICS__ = {
    getState: () => ({
      ready: state.ready,
      seenMediaIds: Array.from(state.seenMediaIds),
      uniqueImagesViewed: state.seenMediaIds.size,
      maxPosition: state.maxPosition,
      pendingEvents: state.pendingPayloads.length,
      reachedScrollThresholds: Array.from(state.reachedScrollThresholds),
      reachedScrollMovementThresholds: Array.from(
        state.reachedScrollMovementThresholds
      ),
      scrollStarted: state.scrollStarted,
      maxScrollPercent: state.maxScrollPercent,
      maxScrollPixels: state.maxScrollPixels,
    }),
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
